/**
 * Wrap a run of Latin digits so it survives an Arabic page.
 *
 * In a right-to-left paragraph the bidi algorithm treats a leading minus, a plus
 * and a trailing unit as neutral characters and reorders them around the number:
 * "-18 cr" renders as "cr 18-" and "108 cr" as "cr 108". The figure is still
 * correct and reads as wrong, which on a page about money is the same thing.
 *
 * U+2066 (LEFT-TO-RIGHT ISOLATE) and U+2069 (POP DIRECTIONAL ISOLATE) are
 * zero-width and tell the algorithm to lay this run out on its own, left to right,
 * without letting it disturb the sentence around it. Doing it in the formatter
 * fixes every number in the product at once — there is no list of call sites to
 * keep up to date, which is the failure mode of the class-per-number approach.
 */
import { en } from "./i18n/en";
import { ar } from "./i18n/ar";

function isolate(text: string) {
  return `\u2066${text}\u2069`;
}

export function formatCredits(value: number) {
  return isolate(new Intl.NumberFormat("en-KW", { maximumFractionDigits: 2 }).format(value));
}

export function formatKd(value: number | null) {
  if (value === null) return "Included";
  return isolate(`${new Intl.NumberFormat("en-KW", { maximumFractionDigits: 2 }).format(value)} KD`);
}

/**
 * A date in the reader's language.
 *
 * It printed "20 Aug 2026" on a page whose own month picker said "أغسطس 2026" —
 * two calendars on one screen, which reads as two systems rather than one. It
 * reads `lang` off the document for exactly the reason `monthLabel` below does:
 * this is called from 38 places, and threading a language through all of them to
 * format a date would cost more than it buys.
 *
 * `-u-nu-latn` keeps Western digits, matching `monthLabel` and every credit figure
 * on the page. Only the month NAME needed translating.
 */
export function formatDate(value: string | null, includeTime = false) {
  if (!value) return word("common.notSet");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return isolate(new Intl.DateTimeFormat(documentLocale(), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date));
}

/** The reader's locale, from the attribute the stylesheet already keys off. */
function documentLocale(): string {
  return typeof document !== "undefined" && document.documentElement.lang === "ar" ? "ar-KW-u-nu-latn" : "en-GB";
}

/**
 * One dictionary word, for the few formatters that must return TEXT and are not
 * components — so they cannot hold the `useT` hook. Same source of truth as `t`,
 * read the same way `monthLabel` reads the language.
 */
function word(key: string): string {
  const dict: Record<string, string> =
    typeof document !== "undefined" && document.documentElement.lang === "ar" ? ar : en;
  return dict[key] ?? key;
}

/**
 * The month name in the reader's language. Reads `lang` off the document rather
 * than taking a parameter, because monthLabel is called from a dozen places that
 * would otherwise each need the language threaded into them — and the document
 * attribute is already the single source of truth the stylesheet keys off.
 */
export function monthLabel(month: string) {
  // `-u-nu-latn` keeps Western digits in the Arabic locale. Plain ar-KW renders
  // ٢٠٢٦, which on its own is correct — but every credit figure on the page is
  // Western, and a statement that mixes ٢٠٢٦ with 108 reads as two systems rather
  // than one. Kuwaiti finance teams read Western digits for money; the month name
  // is what needed translating, not the numerals.
  return new Intl.DateTimeFormat(documentLocale(), { month: "long", year: "numeric" }).format(
    new Date(`${month}-01T12:00:00`),
  );
}

export function contractMonths(start: string, end: string) {
  const months: string[] = [];
  const cursor = new Date(`${start.slice(0, 7)}-01T12:00:00`);
  const final = new Date(`${end.slice(0, 7)}-01T12:00:00`);
  while (cursor <= final) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

/**
 * How a request reached us, as a TRANSLATION KEY rather than a word.
 *
 * It used to capitalise the raw database value and hand back English, which is
 * how "Portal" and "Whatsapp" ended up in the middle of Arabic tables. Returning
 * a key lets the caller translate it.
 *
 * A source with no dictionary entry renders as the KEY — `source.admin` sat in a
 * client-facing ledger column until a screenshot caught it. It is not a graceful
 * fallback and must not be treated as one, so `i18n.test.ts` asserts every source
 * the server can write has an entry in both dictionaries.
 */
export function sourceLabelKey(source: string) {
  const raw = source.startsWith("offline:") ? source.split(":")[1] || "other" : source;
  return `source.${raw}`;
}

/**
 * What a ledger row should SAY, given that some rows were written by a person and
 * some by the system.
 *
 * The system writes English into the database — `title = 'Monthly allocation'`,
 * `note = 'Contracted monthly production credits'` — at the moment a contract
 * month is materialised, long before anybody picks a language. An Arabic client
 * therefore read two English sentences in the middle of an Arabic ledger, and no
 * static check could see it: the string is not in the front-end source at all.
 *
 * The split is by AUTHOR, not by wording:
 *
 *   - `source === "system"` — nobody keyed this in. It is our sentence, so we own
 *     it in both languages and render it from the kind.
 *   - anything else — a manager typed that title and that note. It is theirs. It
 *     renders verbatim, with `dir="auto"`, because translating a person's words is
 *     not localisation.
 *
 * Deriving it at RENDER rather than fixing the writer means rows already in the
 * database read correctly too — there is no migration, and no month where the
 * ledger disagrees with itself.
 */
export function entryText(
  entry: { kind: string; source: string; title: string; note: string | null },
  t: (key: string, vars?: Record<string, string | number>) => string,
): { title: string; note: string; titleAuthored: boolean; noteAuthored: boolean } {
  if (entry.source !== "system") {
    // A spend with no note is the ordinary case — a manager completed the work and
    // typed nothing. So the two halves of one row can have DIFFERENT authors: the
    // title is the client's own request title and must render exactly as they wrote
    // it, while the note underneath is a sentence we supply. One `authored` flag for
    // the row would have to lie about one of them.
    const note = entry.note?.trim();
    if (note) return { title: entry.title, note, titleAuthored: true, noteAuthored: true };
    return {
      title: entry.title,
      note: entry.kind === "spend" ? t("ledger.completedWork") : "",
      titleAuthored: true,
      noteAuthored: false,
    };
  }
  return {
    title: t(`kind.${entry.kind}`),
    // Only the two kinds the system actually writes have a sentence. Any other
    // kind arriving with source=system is a bug upstream, and showing no note
    // beats showing `ledger.note.topup` to a client.
    note: entry.kind === "allocation" || entry.kind === "rollover" ? t(`ledger.note.${entry.kind}`) : "",
    titleAuthored: false,
    noteAuthored: false,
  };
}
